import { useTranslation } from 'react-i18next'
import { useGamification } from '@/features/gamification/hooks/useGamification'
import { BadgeCard } from '@/features/gamification/components/BadgeCard'
import { XpProgress } from '@/features/gamification/components/XpProgress'
import { Leaderboard } from '@/features/social/components/Leaderboard'
import { Alert } from '@/components/ui/Alert'

function RewardsSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="h-32 animate-pulse rounded-3xl bg-surface-container" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6'].map((id) => (
          <div key={id} className="h-40 animate-pulse rounded-2xl bg-surface-container" />
        ))}
      </div>
    </div>
  )
}

export function RewardsPage() {
  const { t, i18n } = useTranslation()
  const { data: profile, isLoading, isError } = useGamification()

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short' }).format(new Date(iso))

  return (
    <div className="min-h-full bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      <h1 className="mb-6 font-display text-2xl font-bold text-on-surface">
        {t('gamification.page_title')}
      </h1>

      {isError && (
        <Alert variant="error" className="mb-6">
          {t('gamification.load_error')}
        </Alert>
      )}

      {isLoading && <RewardsSkeleton />}

      {profile && (
        <div className="flex flex-col gap-8">
          <XpProgress
            xpTotal={profile.xp_total}
            level={profile.level}
            xpToNextLevel={profile.xp_to_next_level}
            progressPercent={profile.progress_percent}
          />

          <section>
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <h2 className="font-display text-lg font-semibold text-on-surface">
                {t('gamification.earned_title')}
              </h2>
              <p className="text-sm text-on-surface-variant">
                {t('gamification.earned_count', {
                  earned: profile.badges_earned.length,
                  total: profile.badges_earned.length + profile.badges_locked.length,
                })}
              </p>
            </div>

            {profile.badges_earned.length === 0 ? (
              <div className="rounded-3xl bg-surface-low p-8 text-center">
                <p className="font-medium text-on-surface">{t('gamification.no_badge')}</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  {t('gamification.no_badge_hint')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {profile.badges_earned.map((badge) => (
                  <BadgeCard
                    key={badge.code}
                    code={badge.code}
                    icon={badge.icon}
                    earnedAt={badge.earned_at}
                  />
                ))}
              </div>
            )}
          </section>

          {profile.badges_locked.length > 0 && (
            <section>
              <h2 className="mb-4 font-display text-lg font-semibold text-on-surface">
                {t('gamification.locked_title')}
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {profile.badges_locked.map((badge) => (
                  <BadgeCard key={badge.code} code={badge.code} icon={badge.icon} />
                ))}
              </div>
            </section>
          )}

          <Leaderboard />

          <section>
            <h2 className="mb-4 font-display text-lg font-semibold text-on-surface">
              {t('gamification.recent_gains')}
            </h2>

            {profile.recent_gains.length === 0 ? (
              <div className="rounded-2xl bg-surface-low px-4 py-6 text-center">
                <p className="text-sm font-medium text-on-surface">{t('gamification.no_gains')}</p>
                <p className="mt-1 text-sm text-on-surface-variant">
                  {t('gamification.no_gains_hint')}
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {profile.recent_gains.map((gain) => (
                  <li
                    key={`${gain.reason}-${gain.earned_at}`}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-surface-low px-4 py-3"
                  >
                    <span className="text-sm text-on-surface">
                      {t(`gamification.reasons.${gain.reason}`)}
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-on-surface-variant">
                        {formatDate(gain.earned_at)}
                      </span>
                      <span className="font-display text-sm font-bold tabular-nums text-secondary">
                        +{gain.amount}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
